import React, { Component } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import NavigationUtil from '../util/NavigationUtil';
import LanguageUtil from '../util/LanguageUtil';
import ApiEditorContainer from '../components/enrichment/api/ApiEditorContainer';
import QueryEditorContainer from '../components/enrichment/query/QueryEditorContainer';
import BasicEditorContainer from '../components/enrichment/basic/BasicEditorContainer';
import EnrichmentListEditorContainer from '../components/enrichment/list/EnrichmentListEditorContainer';
import ConnectionEditorContainer from '../components/connection/connection-detail/ConnectionEditorContainer';
import CaseDetailComponent from '../components/investigation/case/CaseDetailComponent';
import DomainConfigurationComponent from '../components/investigation/configuration/detail/DomainConfigurationComponent';
import AppContainer from 'pages/AppContainer';
import AdminContainer from 'pages/AdminContainer';
import ConnectionContainer from 'pages/ConnectionContainer';
import RuleContainer from '../pages/RuleContainer';
import RuleEditorContainer from '../components/rule/RuleEditorContainer';
import StreamContainer from 'pages/StreamContainer';
import ListContainer from 'pages/ListContainer';
import ListFileEditorContainer from 'components/list/ListFileEditorContainer';
import ListQueryEditorContainer from '../components/list/ListQueryEditorContainer';
import ChannelContainer from '../pages/ChannelContainer';
import AccountContainerComponent from 'components/account/AccountContainerComponent';
import ConfigurationContainer from '../pages/ConfigurationContainer';
import StructContainer from '../pages/StructContainer';
import StructEditorContainer from '../components/struct/detail/StructEditorContainer';
import SandboxContainer from '../pages/SandboxContainer';
import VelocityVariableEditorContainer from '../components/enrichment/velocity/VelocityVariableEditorContainer';
import EnrichmentContainer from '../pages/EnrichmentContainer';
import StreamEditorContainer from '../components/stream/editor/StreamEditorContainer';
import RoleHelper from '../RoleHelper';
import ActionContainer from '../pages/ActionContainer';
import ActionEmailEditorContainer from '../components/action/email/ActionEmailEditorContainer';
import ActionApiEditorContainer from 'components/action/api/ActionApiEditorContainer';
import ActionSlackEditorContainer from '../components/action/slack/ActionSlackEditorContainer';
import ActionDiscordEditorContainer from '../components/action/discord/ActionDiscordEditorContainer';
import ActionJiraEditorContainer from '../components/action/jira/ActionJiraEditorContainer';
import ActionMicrosoftTeamsEditorContainer from '../components/action/microsoft-teams/ActionMicrosoftTeamsEditorContainer';
import ComponentName from '../constant/ComponentName';
import TransactionEditorContainer from 'components/transaction/event-detail/TransactionEditorContainer';
import ComponentCategoryUtil from '../util/ComponenyCategoryUtil';
import NotFound from '../pages/error/NotFound';
import WatchlistContainer from '../pages/WatchlistContainer';
import CustomListContainer from '../pages/CustomListContainer';
import CustomListDetailContainer from '../pages/CustomListDetailContainer';
import CustomListRecordDetailContainer from '../pages/CustomListRecordDetailContainer';
import WatchlistDetailComponent from '../components/kyc/watchlist/WatchlistDetailComponent';
import WatchlistAlertContainer from '../pages/WatchlistAlertContainer';
import WatchlistAlertDetailComponent from '../components/kyc/alert/WatchlistAlertDetailComponent';
import EnvironmentUtil from '../util/EnvironmentUtil';
import { InstanceContainer } from '../config/binding/InstanceBinding';
import Injectables from '../constant/Injectables';
import GraphContainer from '../pages/GraphContainer';
import ChartEditorContainer from '../components/chart/editor/ChartEditorContainer';
import WatchlistTabComponent from '../components/kyc/watchlist/WatchlistTabComponent';
import ActionAddToListEditorContainer from '../components/action/add-to-list/ActionAddToListEditorContainer';
import ActionRemoveFromListEditorContainer from '../components/action/remove-from-list/ActionRemoveFromListEditorContainer';
import WatchlistSocialMediaComponent from '../components/kyc/watchlist/WatchlistSocialMediaComponent';
import DashboardContainer from '../pages/DashboardContainer';
import WatchlistNewsLinkComponent from '../components/kyc/watchlist/WatchlistNewsLinkComponent';
import WatchlistSafeListLinkComponent from '../components/kyc/watchlist/WatchlistSafeListLinkComponent';
import WatchlistRelationDetailComponent from '../components/kyc/detail/WatchlistRelationDetailComponent';
import ActionSendTransactionEditorContainer from '../components/action/send-transaction/ActionSendTransactionEditorContainer';
import AiModelContainer from '../pages/AiModelContainer';
import AiModelEditorContainer from '../components/ai/AiModelEditorContainer';
import TransactionContainer from '../pages/transaction/TransactionContainer';
import TrackerContainer from '../pages/TrackerContainer';
import TrackerEditorContainer from '../components/tracker/TrackerEditorContainer';
import { RootState } from '../redux/store';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import { injectIntl, IntlShape } from 'react-intl';
import { TenantModuleType } from '../domain/rbac/RbacDomains';
import EntityContainer from '../pages/entity/EntityContainer';
import CraPolicyContainer from '../pages/cra/CraPolicyContainer';
import CraAssessmentContainer from '../pages/cra/CraAssessmentContainer';
import AchContainer from '../pages/ach/AchContainer';
import AchFileDetailContainer from '../pages/ach/AchFileDetailContainer';
import AchDetailComponent from '../components/ach/detail/AchDetailComponent';
import WatchlistAlertHistoryDetailComponent from 'components/kyc/alert/WatchlistAlertHistoryDetailComponent';

class AppRouter extends Component<Props> {
  private readonly environmentUtil: EnvironmentUtil = InstanceContainer.get(Injectables.ENVIRONMENT_UTIL);

  constructor(props: Props) {
    super(props);
    LanguageUtil.setIntl(this.props.intl);
  }

  public render() {
    const {
      component: { module },
      user,
    } = this.props;
    const tenantName = NavigationUtil.getTenant();
    let baseName = `${tenantName}`;
    const isFraudOrAml = module.includes('fraud') || module.includes('aml');
    const isKyc = module.includes('kyc');
    const isSandboxVisible = isFraudOrAml && RoleHelper.canView('Sandbox') && this.environmentUtil.getPages().sandbox;
    const isEntityVisible = isFraudOrAml && RoleHelper.canView('Entity') && this.environmentUtil.getPages().entity;
    return (
      <BrowserRouter basename={baseName}>
        <Routes>
          {this.redirectByRole()}
          <Route path='/' element={<AppContainer />}>
            {this.getAccountRoute()}
            {isFraudOrAml && this.getGraphRoute()}
            {isFraudOrAml && RoleHelper.canView('Dashboard') && this.getDashboardRoute()}
            {isFraudOrAml && RoleHelper.canView('Transaction') && this.getTransactionRoute()}
            {isEntityVisible && this.getEntityRoute()}
            {isFraudOrAml && RoleHelper.canView('List') && this.getListRoute()}
            {isFraudOrAml && RoleHelper.canView('Stream') && this.getStreamRoute()}
            {isFraudOrAml && RoleHelper.canView('Enrichment') && this.getEnrichmentRoute()}
            {isFraudOrAml && RoleHelper.canView('Rule') && this.getRuleRoute()}
            {RoleHelper.canView('Admin') && this.getAdminRoute()}
            {isFraudOrAml && RoleHelper.canView('Cases') && this.getCasesRoute()}
            {isKyc && user.activeRiskType == 'compliance' && RoleHelper.canView('Watchlist') && this.environmentUtil.getWatchlist() && this.getWatchlistRoute()}
            {isKyc && user.activeRiskType == 'compliance' && RoleHelper.canView('CustomList') && this.environmentUtil.getWatchlist() && this.getCustomListRoute()}
            {isKyc &&
              user.activeRiskType == 'compliance' &&
              RoleHelper.canView('WatchlistAlert') &&
              this.environmentUtil.getWatchlist() &&
              this.getWatchlistAlertRoute()}
            {RoleHelper.canView('Cra') && this.environmentUtil.getCra() && this.getCraRoute()}
            {isFraudOrAml && RoleHelper.canView('Connection') && this.getConnectionRoute()}
            {RoleHelper.canView('Action') && this.getActionRoute()}
            {isFraudOrAml && RoleHelper.canView('CaseConfiguration') && this.getConfigurationRoute()}
            {isFraudOrAml && RoleHelper.canView('Struct') && this.getStructRoute()}
            {isFraudOrAml && RoleHelper.canView('ACH') && this.environmentUtil.getAch() && this.getAchRoute()}
            {isFraudOrAml && RoleHelper.canView(ComponentName.AI_MODEL) && this.environmentUtil.getAiModel() && this.getAiModelRoute()}
            {isFraudOrAml && this.environmentUtil.getTracker() && this.getTrackerRoute()}
            {isSandboxVisible && this.getSandboxRoute()}
          </Route>
          <Route path={'*'} element={<NotFound />} />
        </Routes>
      </BrowserRouter>
    );
  }

  private redirectByRole() {
    const {
      component: { module },
    } = this.props;
    const isOnlyKyc = module.includes('kyc') && module.filter((item: TenantModuleType) => item !== 'kyc').length === 0;
    const roleToPathMapping: Record<string, string> = {
      Dashboard: '/dashboard',
      Transaction: '/transaction',
      Entity: '/entity',
      List: '/list',
      Stream: '/stream',
      Enrichment: '/enrichment',
      Rule: '/rule',
      Admin: '/admin',
      Cases: '/cases',
      Watchlist: '/watchlist',
      Connection: '/connection',
      Action: '/action',
      CaseConfiguration: '/configuration',
      Struct: '/struct',
      Sandbox: '/sandbox',
      ACH: '/ach',
    };

    return Object.keys(roleToPathMapping)
      .filter((role) => RoleHelper.canView(role))
      .map((role) => <Route key={role} path={'/'} element={<Navigate to={isOnlyKyc ? '/watchlist' : roleToPathMapping[role]} />} />);
  }

  private getAdminRoute() {
    return (
      <Route path='/admin'>
        <Route path='' element={<AdminContainer />} />
        <Route path=':tab' element={<AdminContainer />} />
      </Route>
    );
  }

  private getConnectionRoute() {
    const canEdit = RoleHelper.canEdit(ComponentCategoryUtil.getRoleNamesByComponent(ComponentName.CONNECTION));
    return (
      <Route path='/connection'>
        <Route path='' element={<ConnectionContainer />} />
        <Route path='folder/:folderId' element={<ConnectionContainer />} />
        <Route path='edit/:id' element={<ConnectionEditorContainer />} />
        <Route path='folder/:folderId/edit/:id' element={<ConnectionEditorContainer />} />
        {canEdit && <Route path='create' element={<ConnectionEditorContainer />} />}
        {canEdit && <Route path='folder/:folderId/create' element={<ConnectionEditorContainer />} />}
      </Route>
    );
  }

  private getRuleRoute() {
    const canEdit = RoleHelper.canEdit(ComponentCategoryUtil.getRoleNamesByComponent(ComponentName.RULE));
    return (
      <Route path='/rule'>
        <Route path='' element={<RuleContainer />} />
        <Route path='folder/:folderId' element={<RuleContainer />} />
        <Route path='edit/:id' element={<RuleEditorContainer />} />
        <Route path='folder/:folderId/edit/:id' element={<RuleEditorContainer />} />
        {canEdit && <Route path='create' element={<RuleEditorContainer />} />}
        {canEdit && <Route path='folder/:folderId/create' element={<RuleEditorContainer />} />}
        {canEdit && <Route path={'resolution/create'} element={<RuleEditorContainer />} />}
        {canEdit && <Route path={'folder/:folderId/resolution/create'} element={<RuleEditorContainer />} />}
        {canEdit && <Route path={'monitoring/create'} element={<RuleEditorContainer />} />}
        {canEdit && <Route path={'folder/:folderId/monitoring/create'} element={<RuleEditorContainer />} />}
        <Route path={'resolution/:id'} element={<RuleEditorContainer />} />
        <Route path={'folder/:folderId/resolution/:id'} element={<RuleEditorContainer />} />
        <Route path={'monitoring/:id'} element={<RuleEditorContainer />} />
        <Route path={'folder/:folderId/monitoring/:id'} element={<RuleEditorContainer />} />
      </Route>
    );
  }

  private getEnrichmentRoute() {
    const canEditList = RoleHelper.canEdit(ComponentCategoryUtil.getRoleNamesByComponent(ComponentName.LIST_VARIABLE));
    const canEditApi = RoleHelper.canEdit(ComponentCategoryUtil.getRoleNamesByComponent(ComponentName.API_VARIABLE));
    const canEditQuery = RoleHelper.canEdit(ComponentCategoryUtil.getRoleNamesByComponent(ComponentName.QUERY_VARIABLE));
    const canEditBasic = RoleHelper.canEdit(ComponentCategoryUtil.getRoleNamesByComponent(ComponentName.BASIC_VARIABLE));
    const canEditVelocity = RoleHelper.canEdit(ComponentCategoryUtil.getRoleNamesByComponent(ComponentName.VELOCITY));
    return (
      <Route path='/enrichment'>
        <Route path='' element={<EnrichmentContainer />} />
        <Route path='folder/:folderId' element={<EnrichmentContainer />} />
        {canEditVelocity && <Route path={'velocity/create'} element={<VelocityVariableEditorContainer />} />}
        {canEditVelocity && <Route path={'folder/:folderId/velocity/create'} element={<VelocityVariableEditorContainer />} />}
        {canEditList && <Route path={'list/create'} element={<EnrichmentListEditorContainer />} />}
        {canEditList && <Route path={'folder/:folderId/list/create'} element={<EnrichmentListEditorContainer />} />}
        {canEditApi && <Route path={'api/create'} element={<ApiEditorContainer />} />}
        {canEditApi && <Route path={'folder/:folderId/api/create'} element={<ApiEditorContainer />} />}
        {canEditQuery && <Route path={'query/create'} element={<QueryEditorContainer />} />}
        {canEditQuery && <Route path={'folder/:folderId/query/create'} element={<QueryEditorContainer />} />}
        {canEditBasic && <Route path={'basic/create'} element={<BasicEditorContainer />} />}
        {canEditBasic && <Route path={'folder/:folderId/basic/create'} element={<BasicEditorContainer />} />}
        <Route path={'velocity/:id'} element={<VelocityVariableEditorContainer />} />
        <Route path={'folder/:folderId/velocity/:id'} element={<VelocityVariableEditorContainer />} />
        <Route path={'list/:id'} element={<EnrichmentListEditorContainer />} />
        <Route path={'folder/:folderId/list/:id'} element={<EnrichmentListEditorContainer />} />
        <Route path={'api/:id'} element={<ApiEditorContainer />} />
        <Route path={'folder/:folderId/api/:id'} element={<ApiEditorContainer />} />
        <Route path={'query/:id'} element={<QueryEditorContainer />} />
        <Route path={'folder/:folderId/query/:id'} element={<QueryEditorContainer />} />
        <Route path={'basic/:id'} element={<BasicEditorContainer />} />
        <Route path={'folder/:folderId/basic/:id'} element={<BasicEditorContainer />} />
      </Route>
    );
  }

  private getActionRoute() {
    const canEdit = RoleHelper.canEdit(ComponentCategoryUtil.getRoleNamesByComponent(ComponentName.ACTION));
    return (
      <Route path='/action'>
        <Route path='' element={<ActionContainer />} />
        <Route path='folder/:folderId' element={<ActionContainer />} />
        {canEdit && <Route path={'email/create'} element={<ActionEmailEditorContainer />} />}
        {canEdit && <Route path={'folder/:folderId/email/create'} element={<ActionEmailEditorContainer />} />}
        {canEdit && <Route path={'api/create'} element={<ActionApiEditorContainer />} />}
        {canEdit && <Route path={'folder/:folderId/api/create'} element={<ActionApiEditorContainer />} />}
        {canEdit && <Route path={'slack/create'} element={<ActionSlackEditorContainer />} />}
        {canEdit && <Route path={'folder/:folderId/slack/create'} element={<ActionSlackEditorContainer />} />}
        {canEdit && <Route path={'discord/create'} element={<ActionDiscordEditorContainer />} />}
        {canEdit && <Route path={'folder/:folderId/discord/create'} element={<ActionDiscordEditorContainer />} />}
        {canEdit && <Route path={'jira/create'} element={<ActionJiraEditorContainer />} />}
        {canEdit && <Route path={'folder/:folderId/jira/create'} element={<ActionJiraEditorContainer />} />}
        {canEdit && <Route path={'microsoft-teams/create'} element={<ActionMicrosoftTeamsEditorContainer />} />}
        {canEdit && <Route path={'folder/:folderId/microsoft-teams/create'} element={<ActionMicrosoftTeamsEditorContainer />} />}
        {canEdit && <Route path={'add-to-list/create'} element={<ActionAddToListEditorContainer />} />}
        {canEdit && <Route path={'folder/:folderId/add-to-list/create'} element={<ActionAddToListEditorContainer />} />}
        {canEdit && <Route path={'remove-from-list/create'} element={<ActionRemoveFromListEditorContainer />} />}
        {canEdit && <Route path={'folder/:folderId/remove-from-list/create'} element={<ActionRemoveFromListEditorContainer />} />}
        {canEdit && <Route path={'send-transaction/create'} element={<ActionSendTransactionEditorContainer />} />}
        {canEdit && <Route path={'folder/:folderId/send-transaction/create'} element={<ActionSendTransactionEditorContainer />} />}
        <Route path={'email/:id'} element={<ActionEmailEditorContainer />} />
        <Route path={'folder/:folderId/email/:id'} element={<ActionEmailEditorContainer />} />
        <Route path={'api/:id'} element={<ActionApiEditorContainer />} />
        <Route path={'folder/:folderId/api/:id'} element={<ActionApiEditorContainer />} />
        <Route path={'slack/:id'} element={<ActionSlackEditorContainer />} />
        <Route path={'folder/:folderId/slack/:id'} element={<ActionSlackEditorContainer />} />
        <Route path={'discord/:id'} element={<ActionDiscordEditorContainer />} />
        <Route path={'folder/:folderId/discord/:id'} element={<ActionDiscordEditorContainer />} />
        <Route path={'jira/:id'} element={<ActionJiraEditorContainer />} />
        <Route path={'folder/:folderId/jira/:id'} element={<ActionJiraEditorContainer />} />
        <Route path={'microsoft-teams/:id'} element={<ActionMicrosoftTeamsEditorContainer />} />
        <Route path={'folder/:folderId/microsoft-teams/:id'} element={<ActionMicrosoftTeamsEditorContainer />} />
        <Route path={'add-to-list/:id'} element={<ActionAddToListEditorContainer />} />
        <Route path={'folder/:folderId/add-to-list/:id'} element={<ActionAddToListEditorContainer />} />
        <Route path={'remove-from-list/:id'} element={<ActionRemoveFromListEditorContainer />} />
        <Route path={'folder/:folderId/remove-from-list/:id'} element={<ActionRemoveFromListEditorContainer />} />
        <Route path={'send-transaction/:id'} element={<ActionSendTransactionEditorContainer />} />
        <Route path={'folder/:folderId/send-transaction/:id'} element={<ActionSendTransactionEditorContainer />} />
      </Route>
    );
  }

  private getStreamRoute() {
    const canEdit = RoleHelper.canEdit(ComponentCategoryUtil.getRoleNamesByComponent(ComponentName.STREAM));
    return (
      <Route path='/stream'>
        <Route path='' element={<StreamContainer />} />
        <Route path='folder/:folderId' element={<StreamContainer />} />
        <Route path={'edit/:id'} element={<StreamEditorContainer />} />
        <Route path={'folder/:folderId/edit/:id'} element={<StreamEditorContainer />} />
        {canEdit && <Route path={'create'} element={<StreamEditorContainer />} />}
        {canEdit && <Route path={'folder/:folderId/create'} element={<StreamEditorContainer />} />}
      </Route>
    );
  }

  private getListRoute() {
    const canEditTable = RoleHelper.canEdit('TableList');
    const canEditQuery = RoleHelper.canEdit('QueryList');
    return (
      <Route path='/list'>
        <Route path='' element={<ListContainer />} />
        <Route path='folder/:folderId' element={<ListContainer />} />
        {canEditTable && <Route path='create/file' element={<ListFileEditorContainer />} />}
        {canEditTable && <Route path='folder/:folderId/create/file' element={<ListFileEditorContainer />} />}
        <Route path='file/edit/:id' element={<ListFileEditorContainer />} />
        <Route path='folder/:folderId/file/edit/:id' element={<ListFileEditorContainer />} />
        {canEditQuery && <Route path='create/query' element={<ListQueryEditorContainer />} />}
        {canEditQuery && <Route path='folder/:folderId/create/query' element={<ListQueryEditorContainer />} />}
        <Route path='query/edit/:id' element={<ListQueryEditorContainer />} />
        <Route path='folder/:folderId/query/edit/:id' element={<ListQueryEditorContainer />} />
      </Route>
    );
  }

  private getEntityRoute() {
    return (
      <Route path='/entity'>
        <Route path='' element={<EntityContainer />} />
        <Route path=':domain' element={<EntityContainer />} />
      </Route>
    );
  }

  private getTransactionRoute() {
    const canEdit = RoleHelper.canEdit(ComponentCategoryUtil.getRoleNamesByComponent(ComponentName.TRANSACTION));
    return (
      <Route path='/transaction'>
        <Route path='' element={<ChannelContainer />} />
        <Route path='folder/:folderId' element={<ChannelContainer />} />
        <Route path='edit/:id' element={<TransactionEditorContainer />} />
        <Route path='folder/:folderId/edit/:id' element={<TransactionEditorContainer />} />
        {canEdit && <Route path='create' element={<TransactionEditorContainer />} />}
        {canEdit && <Route path='folder/:folderId/create' element={<TransactionEditorContainer />} />}
      </Route>
    );
  }

  private getAccountRoute() {
    return (
      <Route path='/account'>
        <Route path='' element={<AccountContainerComponent />} />
      </Route>
    );
  }

  private getWatchlistRoute() {
    return (
      <Route path='/watchlist'>
        <Route path='' element={<WatchlistContainer />} />
        <Route path='history/:logId' element={<WatchlistTabComponent />} />
        <Route path=':list/:id' element={<WatchlistTabComponent />} />
        <Route path=':list/news/:id' element={<WatchlistNewsLinkComponent />} />
        <Route path=':list/social-media/:id' element={<WatchlistSocialMediaComponent />} />
        <Route path=':list/safe-list/:id' element={<WatchlistSafeListLinkComponent />} />
        <Route path='detail/:id' element={<WatchlistDetailComponent />} />
        <Route path='relation-detail/:id' element={<WatchlistRelationDetailComponent />} />
      </Route>
    );
  }

  private getCustomListRoute() {
    return (
      <Route path='/custom-list'>
        <Route path='' element={<CustomListContainer />} />
        <Route path=':listId/:recordType/detail/:recordId' element={<CustomListRecordDetailContainer />} />
        <Route path=':listId/:recordType' element={<CustomListDetailContainer />} />
        <Route path=':listId' element={<CustomListDetailContainer />} />
      </Route>
    );
  }

  private getWatchlistAlertRoute() {
    return (
      <Route path='/watchlist-alert'>
        <Route path='' element={<WatchlistAlertContainer />} />
        <Route path='detail/:id' element={<WatchlistAlertDetailComponent />} />
        <Route path='detail/history' element={<WatchlistAlertHistoryDetailComponent />} />
      </Route>
    );
  }

  private getCraRoute() {
    return (
      <Route>
        <Route path='policy' element={<CraPolicyContainer />} />
        <Route path='policy/:id' element={<CraPolicyContainer />} />
        <Route path='assessment' element={<CraAssessmentContainer />} />
        <Route path='assessment/:id' element={<CraAssessmentContainer />} />
      </Route>
    );
  }

  private getDashboardRoute() {
    return (
      <Route path={'/dashboard'}>
        <Route path={''} element={<DashboardContainer />} />
        <Route path=':dashboard/:chartId' element={<ChartEditorContainer />} />
        <Route path=':dashboard/create' element={<ChartEditorContainer />} />
        <Route path=':dashboard' element={<ChartEditorContainer />} />
      </Route>
    );
  }

  private getConfigurationRoute() {
    const canEdit = RoleHelper.canEdit(ComponentCategoryUtil.getRoleNamesByComponent(ComponentName.CONFIGURATION));
    return (
      <Route path='/configuration'>
        <Route path='' element={<ConfigurationContainer />} />
        <Route path='folder/:folderId' element={<ConfigurationContainer />} />
        {canEdit && <Route path='create' element={<DomainConfigurationComponent />} />}
        {canEdit && <Route path='folder/:folderId/create' element={<DomainConfigurationComponent />} />}
        <Route path='edit/:id' element={<DomainConfigurationComponent />} />
        <Route path='folder/:folderId/edit/:id' element={<DomainConfigurationComponent />} />
      </Route>
    );
  }

  private getCasesRoute() {
    return (
      <Route path='/cases'>
        <Route path='' element={<TransactionContainer />} />
        <Route path=':domain' element={<TransactionContainer />} />
        <Route path=':domain/detail/:id' element={<CaseDetailComponent />} />
      </Route>
    );
  }

  private getStructRoute() {
    const canEdit = RoleHelper.canEdit(ComponentCategoryUtil.getRoleNamesByComponent(ComponentName.STRUCT));
    return (
      <Route path='/struct'>
        <Route path='' element={<StructContainer />} />
        <Route path='folder/:folderId' element={<StructContainer />} />
        <Route path='edit/:id' element={<StructEditorContainer />} />
        <Route path='folder/:folderId/edit/:id' element={<StructEditorContainer />} />
        {canEdit && <Route path='create' element={<StructEditorContainer />} />}
        {canEdit && <Route path='folder/:folderId/create' element={<StructEditorContainer />} />}
      </Route>
    );
  }

  private getSandboxRoute() {
    return (
      <Route path={'sandbox'}>
        <Route path='' element={<SandboxContainer />} />
      </Route>
    );
  }

  private getGraphRoute() {
    return (
      <Route path={'/graph'}>
        <Route path='' element={<GraphContainer />} />
        <Route path={':transaction/:filter'} element={<GraphContainer />} />
      </Route>
    );
  }

  private getAiModelRoute() {
    const canEdit = RoleHelper.canEdit(ComponentCategoryUtil.getRoleNamesByComponent(ComponentName.AI_MODEL));
    return (
      <Route path='/ai-model'>
        <Route path='' element={<AiModelContainer />} />
        <Route path='folder/:folderId' element={<AiModelContainer />} />
        <Route path='edit/:id' element={<AiModelEditorContainer />} />
        <Route path='folder/:folderId/edit/:id' element={<AiModelEditorContainer />} />
        {canEdit && <Route path='create' element={<AiModelEditorContainer />} />}
        {canEdit && <Route path='folder/:folderId/create' element={<AiModelEditorContainer />} />}
      </Route>
    );
  }

  private getTrackerRoute() {
    return (
      <Route path='/tracker'>
        <Route path='' element={<TrackerContainer />} />
        <Route path='folder/:folderId' element={<TrackerContainer />} />
        <Route path='edit/:id' element={<TrackerEditorContainer />} />
        <Route path='folder/:folderId/edit/:id' element={<TrackerEditorContainer />} />
        <Route path='create' element={<TrackerEditorContainer />} />
        <Route path='folder/:folderId/create' element={<TrackerEditorContainer />} />
      </Route>
    );
  }

  private getAchRoute() {
    return (
      <Route path='/ach'>
        <Route path='' element={<AchContainer />} />
        <Route path=':fileName' element={<AchFileDetailContainer />} />
        <Route path='detail/:id' element={<AchDetailComponent />} />
      </Route>
    );
  }
}

interface Props extends ReduxProps {
  intl: IntlShape;
}

const mapStateToProps = (state: RootState) => ({
  component: state.component,
  user: state.auth.user,
});
const connector = connect(mapStateToProps);
type ReduxProps = ConnectedProps<typeof connector>;

export default injectIntl(connector(AppRouter));
